<template>
    <div class="mt-6 content">
        <div class="columns is-gapless is-mobile">
            <div class="column is-narrow"><i class="fad fa-fw fa-flag-swallowtail has-text-white mr-3 is-size-4"></i></div>
            <div class="column">
                <h3 class="has-text-white">Milestones & Reflections</h3>
                <p class="has-text-white">Milestones act as checkpoints during your coursework. As you progress through your course you may be stopped to complete one of these checkpoints.</p>

                <div class="columns is-gapless mb-5 mt-5">
                    <div class="column is-narrow has-text-white"><i class="far fa-fw fa-info-circle mr-2"></i></div>
                    <div class="column has-text-white">
                        <div>Please note that these Milestones & Reflections, along with your Study Log, are further documentation of your coursework. We need this documentation when we review your course and it's required should your course be selected for audit. It is important that your reflections are authentic and thoughtful. Repetitive (or copy/pasted) reflections will be rejected and you will be asked to rewrite them or provide additional artifacts to document your course.</div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Loading -->
        <div class="mt-6 pb-6" v-if="loading">
            <ui-card class="pt-0 pl-4 pr-6">
                <div class="py-6 has-text-centered">
                    <i class="fas fa-spin fa-spinner fa-fw mr-2"></i>Loading milestones...
                </div>
            </ui-card>
        </div>

        <!-- Not a self-directed course -->
        <div class="mt-6 pb-6" v-else-if="!isSelfDirected">
            <ui-card class="pt-0 pl-4 pr-6">
                <div class="columns is-gapless mb-5 pt-5">
                    <div class="column is-narrow"><i class="far fa-fw fa-info-circle mr-2"></i></div>
                    <div class="column">
                        <div class="has-text-weight-bold">This course does not have any required milestones or reflections.</div>
                    </div>
                </div>
            </ui-card>
        </div>

        <!-- Milestones loaded -->
        <div class="mt-6 pb-6" v-else>
            <!-- Current Milestone - Working toward it (show instructions, no form yet) -->
            <ui-card class="mb-6 pr-6" v-if="current && current.status === 'pending' && !isLocked">
                <div class="columns is-gapless mb-5 pt-5">
                    <div class="column is-narrow"><i class="far fa-fw fa-flag-swallowtail mr-2 is-size-5 has-text-info"></i></div>
                    <div class="column">
                        <h4 class="has-text-weight-bold">Milestone {{ current.milestone_number }}: {{ getMilestoneRange(current) }}</h4>
                        <p class="has-text-grey-dark mb-4">You've logged <strong>{{ totalHours.toFixed(1) }} hours</strong>. Your next checkpoint is at <strong>{{ current.hour_threshold }} hours</strong>.</p>

                        <progress class="progress is-info mt-4 mb-5" :value="totalHours" :max="current.hour_threshold"></progress>

                        <!-- Instructions from admin -->
                        <div v-if="current.instructions">
                            <label class="label has-text-grey">Instructions for this milestone:</label>
                            <div class="notification is-light">
                                <div v-html="current.instructions"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </ui-card>

            <!-- Current Milestone - Reached threshold, needs reflection -->
            <ui-card class="mb-6 pr-6" v-else-if="current && current.status === 'pending' && isLocked">
                <div class="columns is-gapless mb-5 pt-5">
                    <div class="column is-narrow"><i class="far fa-fw fa-location-check mr-2 is-size-5 has-text-warning"></i></div>
                    <div class="column">
                        <h4 class="has-text-weight-bold">Milestone {{ current.milestone_number }}: {{ getMilestoneRange(current) }}</h4>
                        <p class="has-text-grey-dark mb-4">You've reached {{ totalHours.toFixed(1) }} hours. Please complete the following and submit your reflection to continue.</p>

                        <!-- Instructions from admin -->
                        <div v-if="current.instructions" class="mb-5">
                            <div class="notification is-light">
                                <div v-html="current.instructions"></div>
                            </div>
                        </div>

                        <div class="mt-5">
                            <b-field label="Your Reflection">
                                <b-input
                                    type="textarea"
                                    v-model="reflectionText"
                                    placeholder="Please write a thoughtful reflection about your progress, what you've learned, and how you're applying it..."
                                    rows="6"
                                ></b-input>
                            </b-field>
                        </div>

                        <b-button
                            type="is-success"
                            class="mt-5"
                            @click="submitReflection"
                            :loading="submitting"
                            :disabled="!reflectionText.trim()"
                        >
                            <strong>Submit Reflection</strong>
                            <i class="far fa-arrow-right-long ml-3"></i>
                        </b-button>
                    </div>
                </div>
            </ui-card>

            <!-- Current Milestone - Submitted (pending review) -->
            <ui-card class="mb-6 pr-6" v-else-if="current && current.status === 'submitted'">
                <div class="columns is-gapless mb-5 pt-5">
                    <div class="column is-narrow"><i class="far fa-fw fa-clock mr-2 is-size-5 has-text-info"></i></div>
                    <div class="column">
                        <h4 class="has-text-weight-bold">Milestone {{ current.milestone_number }}: {{ getMilestoneRange(current) }}</h4>
                        <div class="notification is-info is-light mt-4">
                            <i class="fas fa-clock mr-2"></i>
                            <strong>Pending Review</strong>
                            <p class="mt-2">Your reflection has been submitted and is awaiting review by an administrator. You'll be notified once it's approved.</p>
                        </div>
                        <div class="mt-4">
                            <label class="label is-size-7 has-text-grey">Your submitted reflection:</label>
                            <p class="content-text">{{ current.reflection_text }}</p>
                        </div>
                    </div>
                </div>
            </ui-card>

            <!-- Current Milestone - Changes Requested -->
            <ui-card class="mb-6 pr-6" v-else-if="current && current.status === 'changes_requested'">
                <div class="columns is-gapless mb-5 pt-5">
                    <div class="column is-narrow"><i class="far fa-fw fa-exclamation-triangle mr-2 is-size-5 has-text-warning"></i></div>
                    <div class="column">
                        <h4 class="has-text-weight-bold">Milestone {{ current.milestone_number }}: {{ getMilestoneRange(current) }}</h4>

                        <div class="notification is-warning is-light mt-4">
                            <i class="fas fa-comment-dots mr-2"></i>
                            <strong>Changes Requested</strong>
                            <p class="mt-2">{{ current.admin_feedback }}</p>
                        </div>

                        <!-- Instructions from admin -->
                        <div v-if="current.instructions" class="mb-5">
                            <label class="label is-size-7 has-text-grey">Original instructions:</label>
                            <div class="notification is-light">
                                <div v-html="current.instructions"></div>
                            </div>
                        </div>

                        <div class="mt-4">
                            <label class="label is-size-7 has-text-grey">Your previous reflection:</label>
                            <p class="content-text mb-4">{{ current.reflection_text }}</p>
                        </div>

                        <div class="mt-5">
                            <b-field label="Updated Reflection">
                                <b-input
                                    type="textarea"
                                    v-model="reflectionText"
                                    placeholder="Please update your reflection based on the feedback..."
                                    rows="6"
                                ></b-input>
                            </b-field>
                        </div>

                        <b-button
                            type="is-success"
                            class="mt-5"
                            @click="submitReflection"
                            :loading="submitting"
                            :disabled="!reflectionText.trim()"
                        >
                            <strong>Resubmit Reflection</strong>
                            <i class="far fa-arrow-right-long ml-3"></i>
                        </b-button>
                    </div>
                </div>
            </ui-card>

            <!-- All milestones complete -->
            <ui-card class="mb-6 pr-6" v-else-if="!current && milestones.length > 0">
                <div class="columns is-gapless mb-5 pt-5">
                    <div class="column is-narrow"><i class="far fa-fw fa-trophy mr-2 is-size-5 has-text-success"></i></div>
                    <div class="column">
                        <h4 class="has-text-weight-bold">All Milestones Complete!</h4>
                        <p>You've completed all <strong>{{ milestones.length }}</strong> milestones for this course. Total hours logged: <strong>{{ totalHours.toFixed(1) }}</strong>.</p>
                    </div>
                </div>
            </ui-card>

            <!-- Completed Milestones History -->
            <template v-if="completedMilestones.length > 0">
                <h4 class="has-text-white mt-6 mb-4"><i class="far fa-history mr-2"></i>Completed Milestones</h4>

                <ui-card class="mb-4 pr-6" v-for="m in completedMilestones" :key="m.id">
                    <div class="columns is-gapless pt-4 pb-2">
                        <div class="column is-narrow"><i class="far fa-fw fa-check-circle mr-2 has-text-success"></i></div>
                        <div class="column">
                            <h5 class="has-text-weight-bold mb-2">Milestone {{ m.milestone_number }}: {{ getMilestoneRange(m) }}</h5>
                            <p class="is-size-7 has-text-grey mb-3">Approved {{ formatDate(m.reviewed_at) }}</p>

                            <b-collapse :open="false" aria-id="milestone-collapse">
                                <template #trigger="props">
                                    <a class="is-size-7">
                                        <i :class="props.open ? 'fa-chevron-up' : 'fa-chevron-down'" class="fas mr-1"></i>
                                        {{ props.open ? 'Hide details' : 'Show details' }}
                                    </a>
                                </template>
                                <div class="mt-3">
                                    <label class="label is-size-7 has-text-grey">Your reflection:</label>
                                    <p class="content-text">{{ m.reflection_text }}</p>
                                </div>
                            </b-collapse>
                        </div>
                    </div>
                </ui-card>
            </template>
        </div>
    </div>
</template>

<script>
    import {client as http} from "../../http_client";
    import moment from "moment";
    import UiCard from "../../components/UiCard";

    export default {
        components: {UiCard},
        props: ['enrollmentId'],
        data: function() {
            return {
                loading: true,
                milestones: [],
                current: null,
                totalHours: 0,
                isLocked: false,
                isSelfDirected: false,
                reflectionText: '',
                submitting: false
            };
        },
        computed: {
            completedMilestones() {
                return this.milestones
                    .filter(m => m.status === 'approved')
                    .sort((a, b) => b.milestone_number - a.milestone_number);
            }
        },
        methods: {
            loadMilestones: function() {
                this.loading = true;
                http.get(`/app/enrollments/${this.enrollmentId}/milestones`, {force: true}).then((response) => {
                    this.milestones = response.data.milestones || [];
                    this.current = response.data.current;
                    this.totalHours = response.data.total_hours || 0;
                    this.isLocked = response.data.is_locked;
                    this.isSelfDirected = response.data.is_self_directed;

                    // Pre-fill reflection text if changes were requested
                    if (this.current && this.current.status === 'changes_requested') {
                        this.reflectionText = this.current.reflection_text || '';
                    } else {
                        this.reflectionText = '';
                    }

                    this.loading = false;
                }).catch((error) => {
                    console.error('Error loading milestones:', error);
                    this.loading = false;
                });
            },
            submitReflection: function() {
                if (!this.reflectionText.trim()) return;

                this.submitting = true;
                http.post(`/app/enrollments/${this.enrollmentId}/milestones/${this.current.id}/submit`, {
                    reflection_text: this.reflectionText.trim()
                }).then(() => {
                    this.$buefy.toast.open({
                        message: 'Reflection submitted successfully!',
                        type: 'is-success'
                    });
                    this.loadMilestones();
                }).catch((error) => {
                    console.error('Error submitting reflection:', error);
                    this.$buefy.toast.open({
                        message: error.response?.data?.error || 'Error submitting reflection',
                        type: 'is-danger'
                    });
                }).finally(() => {
                    this.submitting = false;
                });
            },
            getMilestoneRange: function(milestone) {
                const prevThreshold = this.milestones
                    .filter(m => m.milestone_number < milestone.milestone_number)
                    .reduce((max, m) => Math.max(max, m.hour_threshold), 0);
                return `${prevThreshold}-${milestone.hour_threshold} Hours`;
            },
            formatDate: function(date) {
                return date ? moment(date).format('MMMM Do, YYYY') : '';
            }
        },
        mounted() {
            this.loadMilestones();
        }
    };
</script>

<style scoped>
    .content-text {
        white-space: pre-wrap;
        line-height: 1.6;
    }
</style>
